import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Stack, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, TextField, MenuItem, Chip, Tooltip,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment,
  FormControlLabel, Switch, Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import {
  ITemplateVariable, ITemplateVariableInput, VariableStatusFilter, VARIABLE_CATEGORIES,
  fetchTemplateVariables, createTemplateVariable, updateTemplateVariable,
  deleteTemplateVariable, setTemplateVariableActive, normalizeVariableKey,
} from "../../../Api/templateVariablesApi";
import { Loader } from "../../../Components/Loader";

const NAVY = "#0F172A";
const HAIRLINE = "rgba(10,14,26,0.06)";
const STATUS_COLORS = {
  active: { bg: "rgba(22,163,74,0.10)", color: "#16a34a", border: "rgba(22,163,74,0.25)" },
  inactive: { bg: "rgba(100,116,139,0.10)", color: "#64748b", border: "rgba(100,116,139,0.25)" },
};

interface FormState {
  id: number | null;
  name: string;
  key: string;
  keyTouched: boolean;
  category: string;
  sample_value: string;
  description: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  id: null, name: "", key: "", keyTouched: false,
  category: "Customer", sample_value: "", description: "", is_active: true,
};

const TemplateVariablesPage: React.FC = () => {
  const [rows, setRows] = useState<ITemplateVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("");
  const [status, setStatus] = useState<VariableStatusFilter>("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ITemplateVariable | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" }>(
    { open: false, msg: "", sev: "success" },
  );

  const notify = (msg: string, sev: "success" | "error" = "success") =>
    setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTemplateVariables({
        search: search || undefined,
        category: category || undefined,
        status,
      });
      setRows(data);
    } catch (e: any) {
      notify(e?.message || "Failed to load variables", "error");
    } finally {
      setLoading(false);
    }
  }, [search, category, status]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search/filter
    return () => clearTimeout(t);
  }, [load]);

  const categoriesInUse = useMemo(() => {
    const set = new Set<string>(VARIABLE_CATEGORIES);
    rows.forEach((r) => set.add(r.category));
    return Array.from(set).sort();
  }, [rows]);

  const openCreate = () => { setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (v: ITemplateVariable) => {
    setForm({
      id: v.id, name: v.name, key: v.key, keyTouched: true,
      category: v.category, sample_value: v.sample_value || "",
      description: v.description || "", is_active: v.is_active,
    });
    setDialogOpen(true);
  };

  const onNameChange = (name: string) =>
    setForm((f) => ({ ...f, name, key: f.keyTouched ? f.key : normalizeVariableKey(name) }));
  const onKeyChange = (key: string) =>
    setForm((f) => ({ ...f, key: normalizeVariableKey(key), keyTouched: true }));

  const save = async () => {
    if (!form.name.trim()) return notify("Name is required", "error");
    if (!form.key.trim()) return notify("Key is required", "error");
    if (!form.category.trim()) return notify("Category is required", "error");
    setSaving(true);
    try {
      const payload: ITemplateVariableInput = {
        name: form.name.trim(),
        key: form.key.trim(),
        category: form.category.trim(),
        sample_value: form.sample_value.trim() || null,
        description: form.description.trim() || null,
        is_active: form.is_active,
      };
      if (form.id) {
        await updateTemplateVariable(form.id, payload);
        notify("Variable updated.");
      } else {
        await createTemplateVariable(payload);
        notify("Variable created.");
      }
      setDialogOpen(false);
      load();
    } catch (e: any) {
      notify(e?.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v: ITemplateVariable) => {
    try {
      await setTemplateVariableActive(v.id, !v.is_active);
      notify(!v.is_active ? "Variable activated." : "Variable disabled.");
      load();
    } catch (e: any) {
      notify(e?.message || "Failed", "error");
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplateVariable(deleteTarget.id);
      notify("Variable deleted.");
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      notify(e?.message || "Delete failed", "error");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Template Variables</Typography>
          <Typography variant="body2" color="text.secondary">
            {rows.length} variable{rows.length === 1 ? "" : "s"} · used as {"{{tokens}}"} in email & SMS templates
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" } }}>
          New Variable
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ mt: 2, borderColor: HAIRLINE }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ p: 2 }}>
          <TextField
            size="small" placeholder="Search by name or key" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <TextField select size="small" label="Category" value={category}
            onChange={(e) => setCategory(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All categories</MenuItem>
            {categoriesInUse.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={status}
            onChange={(e) => setStatus(e.target.value as VariableStatusFilter)} sx={{ minWidth: 150 }}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: NAVY, borderColor: HAIRLINE } }}>
                <TableCell>Name</TableCell>
                <TableCell>Token</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sample Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><Loader /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No variables found.
                </TableCell></TableRow>
              ) : rows.map((v) => {
                const sc = v.is_active ? STATUS_COLORS.active : STATUS_COLORS.inactive;
                return (
                  <TableRow key={v.id} hover sx={{ "& td": { borderColor: HAIRLINE } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, color: NAVY }}>{v.name}</Typography>
                      {v.description && <Typography variant="caption" color="text.secondary">{v.description}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={v.token} size="small" sx={{ fontFamily: "monospace", bgcolor: "rgba(15,23,42,0.05)" }} />
                    </TableCell>
                    <TableCell><Chip label={v.category} size="small" variant="outlined" /></TableCell>
                    <TableCell sx={{ maxWidth: 220, color: "text.secondary" }} title={v.sample_value || ""}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {v.sample_value || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Chip label={v.is_active ? "Active" : "Inactive"} size="small"
                        sx={{ bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={v.is_active ? "Disable" : "Activate"}>
                        <IconButton size="small" onClick={() => toggleActive(v)}
                          sx={{ color: v.is_active ? "#16a34a" : "#94a3b8" }}>
                          <PowerSettingsNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteTarget(v)} sx={{ color: "#dc2626" }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>
          {form.id ? "Edit Variable" : "New Variable"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField label="Variable Name *" value={form.name} fullWidth
              onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Customer Name" />
            <TextField label="Key *" value={form.key} fullWidth
              onChange={(e) => onKeyChange(e.target.value)}
              helperText={<>Token: <code>{`{{${form.key || "key"}}}`}</code> — lowercase letters, numbers, underscores</>}
            />
            <Autocomplete freeSolo options={VARIABLE_CATEGORIES} value={form.category}
              onChange={(_, val) => setForm((f) => ({ ...f, category: val || "" }))}
              onInputChange={(_, val) => setForm((f) => ({ ...f, category: val }))}
              renderInput={(params) => <TextField {...params} label="Category *" />}
            />
            <TextField label="Sample Value" value={form.sample_value} fullWidth
              onChange={(e) => setForm((f) => ({ ...f, sample_value: e.target.value }))}
              placeholder="Shown in template preview" />
            <TextField label="Description" value={form.description} fullWidth multiline minRows={2}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            <FormControlLabel
              control={<Switch checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
              label="Active (available in the template picker)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            sx={{ bgcolor: NAVY, "&:hover": { bgcolor: "#1e293b" } }}>
            {saving ? "Saving…" : form.id ? "Save Changes" : "Create Variable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>Delete variable?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong> (<code>{deleteTarget?.token}</code>)? Templates using this
            token will keep the literal text until updated.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateVariablesPage;
